import { abyssColors, primary } from '@/theme/colors';
import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { tickStyles } from '../../styles';

interface TableResult {
  title: string;
  value: string;
  comment: string;
}

export const TransitionPeriodGraph = ({ data = [] }: { data: TableResult[] }) => {
  const showGraph = useMemo(() => {
    return data.some((row) => row.title.includes('Transition Date '));
  }, [data]);

  const transitionPeriods = useMemo(() => {
    return data
      .filter((row) => row.value && row.title.includes('Transition Date '))
      .sort((a, b) => a.value.localeCompare(b.value))
      .reverse()
      .map((row, index) => ({
        transitionDate: row.value,
        damageClass: 5 - index,
      }))
      .reverse();
  }, [data]);

  const serverityZone = useMemo(() => {
    const count = transitionPeriods.length;
    return count > 2 ? ((count - 2) / (count - 1)) * 100 : 0;
  }, [transitionPeriods]);

  return (
    <>
      {showGraph && transitionPeriods.length > 0 && (
        <Stack>
          <Typography fontWeight={500} color={abyssColors.primary[500]}>
            Transition period graphical representation:
          </Typography>
          <Box
            width="100%"
            my={'1.6rem'}
            padding={'2rem'}
            sx={{
              border: `1px solid ${abyssColors.primary[200]}`,
              borderRadius: '0.8rem',
            }}
          >
            <ResponsiveContainer height={220}>
              <LineChart data={transitionPeriods} margin={{ left: -8, right: 16, bottom: 0 }}>
                <CartesianGrid
                  vertical={false}
                  horizontal
                  color={abyssColors.primary[200]}
                  opacity={0.5}
                  strokeWidth={0.5}
                />
                <defs>
                  <linearGradient id="gradient" x1="0" y1="0" x2="100%" y2="0">
                    <stop offset="0%" stopColor={primary.LightBlue} />
                    <stop offset={`${serverityZone}%`} stopColor={primary.LightBlue} />
                    <stop offset={`${serverityZone}%`} stopColor={abyssColors.error[700]} />
                    <stop offset="100%" stopColor={abyssColors.error[700]} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="transitionDate"
                  xAxisId="0"
                  tickLine={false}
                  tickMargin={18}
                  axisLine={false}
                  tick={{
                    ...tickStyles,
                    fontSize: '10px',
                  }}
                  tickFormatter={(value) => new Date(value).getFullYear().toString()}
                />
                <XAxis
                  dataKey="transitionDate"
                  xAxisId="1"
                  axisLine={false}
                  tickLine={false}
                  tickMargin={0}
                  tick={tickStyles}
                  tickFormatter={(value) =>
                    new Date(value).toLocaleString('default', { month: 'short' })
                  }
                />
                <YAxis
                  tickFormatter={(value) => `DC${value}`}
                  domain={[1, 6]}
                  ticks={[1, 2, 3, 4, 5]}
                  axisLine={false}
                  tickLine={false}
                  tickMargin={24}
                  tick={tickStyles}
                />
                <Line
                  type="monotone"
                  dataKey="damageClass"
                  stroke="url(#gradient)"
                  data={transitionPeriods}
                  strokeWidth={3}
                  opacity={0.3}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="damageClass"
                  data={[transitionPeriods[0]]}
                  color={primary.LightBlue}
                  strokeWidth={1}
                  dot={{ fill: primary.LightBlue }}
                />
                {transitionPeriods.length > 2 && (
                  <ReferenceLine
                    x={transitionPeriods.at(-2)?.transitionDate}
                    strokeDasharray={'1 4'}
                    stroke="#bbb"
                    color="#bbb"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Stack>
      )}
    </>
  );
};
